"use client";

import {
  Position,
  type ConnectionLineComponentProps,
} from "@xyflow/react";
import { roundedPath } from "@/lib/cable-routing";
import { useCableRouter } from "@/components/hooks/use-cable-router";
import { useContext } from "react";
import { ConnectionPreviewContext, RoutingSceneContext } from "./connection-preview";

/** Aim the target stub back toward the source — only when the cursor is not over
 *  a real handle. */
function targetSideFromCursor(
  fromX: number,
  fromY: number,
  toX: number,
  toY: number,
): Position {
  const dx = fromX - toX;
  const dy = fromY - toY;
  if (Math.abs(dx) >= Math.abs(dy)) {
    return dx >= 0 ? Position.Right : Position.Left;
  }
  return dy >= 0 ? Position.Bottom : Position.Top;
}

/** Drawn through the same router as a finished cable, so the drag matches the drop. */
export function CableConnectionLine({
  fromX,
  fromY,
  toX,
  toY,
  fromPosition,
  toPosition,
  toHandle,
  fromNode,
  toNode,
  connectionStatus,
}: ConnectionLineComponentProps) {
  const preview = useContext(ConnectionPreviewContext);
  const cableRouter = useCableRouter();
  const scene = useContext(RoutingSceneContext);
  // React Flow owns exact handle hits. The body-drop fallback uses the same
  // compatible terminal in this preview and in the eventual connection.
  const bodyTarget = !toHandle ? preview : null;
  const end = bodyTarget?.point ?? { x: toX, y: toY };
  const sourceSide = fromPosition ?? Position.Right;
  // React Flow's default target side is "opposite of source", which draws a hairpin.
  const targetSide = toHandle
    ? toPosition
    : bodyTarget
      ? { left: Position.Left, right: Position.Right, top: Position.Top, bottom: Position.Bottom }[bodyTarget.side]
      : targetSideFromCursor(fromX, fromY, toX, toY);

  const points = cableRouter.connection({
    source: { x: fromX, y: fromY }, target: end, sourceSide, targetSide,
    scene, endpoints: { fromNodeId: fromNode?.id ?? "", toNodeId: toNode?.id ?? bodyTarget?.nodeId ?? "" },
  });
  const d = roundedPath(points, 14);

  const stroke =
    connectionStatus === "invalid"
      ? "var(--color-danger, hsl(0 70% 50%))"
      : connectionStatus === "valid"
        ? "var(--color-success, hsl(140 50% 40%))"
        : "var(--color-fg-muted)";

  return (
    <g className="react-flow__connection">
      <path
        d={d}
        fill="none"
        stroke={stroke}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity={0.85}
      />
      <circle cx={end.x} cy={end.y} r={bodyTarget ? 6 : 3} fill={stroke} />
    </g>
  );
}
